import { Box, Flex, Select, Text, Title, useMantineTheme } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { useLocalStorage } from "@uidotdev/usehooks";
import { useEffect, useMemo, useState } from "react";
import MetadataDocuments from "src/components/MetadataDocuments";
import type { ChatVectorIndexShorthand, FSR, SF } from "src/db";
import { useDatasources } from "src/hooks/useDatasources";
import { type DatasourceIngestionStatus, useIngestionStatus } from "src/hooks/useIngestionStatus";

export const DocumentsRoute = ({
    index,
    setSelectedIndex,
}: {
    index: ChatVectorIndexShorthand;
    setSelectedIndex: React.Dispatch<React.SetStateAction<ChatVectorIndexShorthand>>;
}) => {
    const navigate = useNavigate();
    const theme = useMantineTheme();
    const [filterResetKey, setFilterResetKey] = useState(0);
    const [filteredDocumentCount, setFilteredDocumentCount] = useState<number | undefined>(
        undefined,
    );
    const [storedUserDatasources, _] = useLocalStorage<string[]>("userDatasources", []);
    const { data: chatVectorIndex = [] } = useDatasources();
    const titles = useMemo(
        () =>
            Object.fromEntries(chatVectorIndex.map((o) => [o.shorthand, o.label])) as Record<
                ChatVectorIndexShorthand,
                string
            >,
        [chatVectorIndex],
    );

    useEffect(() => {
        if (window.location.hash === "#/documents") navigate({ to: `/documents?tab=${index}` });
    }, [index, navigate]);

    const { data: ingestionStatus } = useIngestionStatus();
    const datasourceIdentifier = chatVectorIndex.find((value) => value.shorthand === index)?.value;
    const ingestionStatusForDatasource = Array.isArray(ingestionStatus?.data_sources)
        ? ingestionStatus?.data_sources.find((value) => value.id === datasourceIdentifier)
        : undefined;

    const formatDocumentCount = (count: number): string => {
        return count.toLocaleString();
    };

    const ingestionStatusText = (
        status: DatasourceIngestionStatus | undefined,
    ): string | undefined => {
        if (!status) return undefined;

        const isFiltered = filteredDocumentCount !== undefined;

        if (isFiltered) {
            const count = filteredDocumentCount;
            return count > 0
                ? `Showing (${formatDocumentCount(count)}) ${count === 1 ? "document" : "documents"}`
                : "No documents found";
        }

        return status.total_count > 0
            ? `${status.ingestion_status} (${status.total_count} documents)`
            : `${status.ingestion_status}`;
    };
    const normalizeString = (str: string) => str.toLowerCase().replace(/[_\s]/g, "");

    const getAvailableIndicesInUserRoles = () => {
        const availableIndices = chatVectorIndex.filter((vectorIndex) =>
            storedUserDatasources.some(
                (userDs) =>
                    normalizeString(userDs).includes(normalizeString(vectorIndex.label)) ||
                    normalizeString(vectorIndex.label).includes(normalizeString(userDs)) ||
                    // Also check if backend datasource ID matches the shorthand
                    normalizeString(userDs) === normalizeString(vectorIndex.shorthand),
            ),
        );
        return availableIndices;
    };

    return (
        <Flex p="xl" direction="column" gap="md">
            <Flex justify="space-between">
                <Flex align={"baseline"} gap={"xs"} wrap={"wrap"}>
                    <Title lh={2} c="purple.8" order={2} style={{ textTransform: "uppercase" }}>
                        {titles[index]}
                    </Title>
                    {ingestionStatusForDatasource && (
                        <Text size="sm" fw={600} color="purple.7">
                            {ingestionStatusText(ingestionStatusForDatasource)}
                        </Text>
                    )}
                </Flex>
                <Box mt="xs" maw={300}>
                    <Select
                        id="data-sources"
                        value={index}
                        onChange={async (value: string | null) => {
                            if (value) {
                                setSelectedIndex(value as typeof SF | typeof FSR);
                                navigate({ to: `/documents?tab=${value}` });
                                setFilterResetKey((prev) => prev + 1);
                            }
                        }}
                        data={getAvailableIndicesInUserRoles()
                            .filter(
                                (item) =>
                                    item.shorthand !== "combined" && item.shorthand !== "emails",
                            )
                            .map((item) => ({
                                label: item.label,
                                value: item.shorthand,
                            }))}
                        placeholder="Select a data source"
                        nothingFoundMessage="No data sources found"
                        styles={{
                            input: {
                                fontWeight: "bold",
                                color: theme.colors.purple[7],
                            },
                            dropdown: { maxHeight: 200, overflowY: "auto" },
                        }}
                        withScrollArea={false}
                        size="sm"
                        w={300}
                    />
                </Box>
            </Flex>
            <Box>
                <MetadataDocuments
                    index={index}
                    key={filterResetKey}
                    onFilteredCountChange={setFilteredDocumentCount}
                />
            </Box>
        </Flex>
    );
};
